import { useEffect, useState } from "react"
import { createPortal } from "react-dom"

import { ApiTypes, UITypes } from "types"
import styles from "assets/scss/index.module.scss"
import { ReactComponent as IconInfo } from "assets/svg/icon-info-message.svg"

import { createReferralSchema, onFormValidate, validationInitialState } from "utils/onValidate"
import { ModalLayout } from "layouts/ModalLayout"

import { modalTexts, ReferralModalMode } from "./modalTexts"
import { ReferralFormFields, ReferralFormValues } from "./ReferralFormFields"
import { Header, InfoBanner, Subtitle, Title } from "./shared"

export type { ReferralModalMode }

interface Props {
  mode: ReferralModalMode
  referral?: ApiTypes.Model.Referral
  onCancel: () => void
  onSave: (values: ApiTypes.Req.CreateReferral) => void
}

const toOption = (item?: { id: number; name: string } | null): UITypes.Option[] =>
  item ? [{ value: item.id, label: item.name }] : []

export const ReferralModal = ({ mode, referral, onCancel, onSave }: Props) => {
  const texts = modalTexts[mode]

  const [error, setError] = useState(validationInitialState)
  const [values, setValues] = useState<ReferralFormValues>({
    homeowner_name: referral?.homeowner_name ?? "",
    phone: referral?.homeowner_phone ?? "",
    email: referral?.homeowner_email ?? "",
    thermostat_quantity: referral?.thermostat_quantity ?? 1,
    address1: referral?.property_address1 ?? "",
    city: toOption(referral?.city),
    state: toOption(referral?.state),
  })

  useEffect(() => {
    if (mode === "review") onFormValidate(values, createReferralSchema, setError)
  }, [])

  const onSubmit = (event: React.FormEvent<HTMLElement>) => {
    event.preventDefault()
    if (!onFormValidate(values, createReferralSchema, setError)) return

    onSave({
      homeowner_name: values.homeowner_name.trim(),
      homeowner_email: values.email.trim() || undefined,
      homeowner_phone: values.phone.trim() || undefined,
      property_address1: values.address1.trim(),
      state_id: (values.state[0]?.value as number) ?? undefined,
      city_id: (values.city[0]?.value as number) ?? undefined,
      thermostat_quantity: values.thermostat_quantity,
    })
  }

  return createPortal(
    <ModalLayout
      width="640px"
      confirmButtonText={texts.confirm}
      cancelButtonText="Close"
      confirmButtonType="submit"
      onConfirm={onSubmit}
      onCancel={onCancel}
    >
      <div className={styles.modalContent}>
        <Header>
          <Title>{texts.title}</Title>
          <Subtitle>{texts.subtitle}</Subtitle>
        </Header>
        <ReferralFormFields values={values} error={error} setValues={setValues} />
        {texts.info && (
          <InfoBanner>
            <IconInfo />
            <span>{texts.info}</span>
          </InfoBanner>
        )}
      </div>
    </ModalLayout>,
    document.body,
  )
}
